/**
 * ============================================================================
 * @deprecated  ENG-01 (2026-04-30) — La matemática financiera fue migrada
 * a PostgreSQL como Single Source of Truth.
 *
 *   ▸ Use el RPC `calculate_landed_cost_preview(p_org_id, p_input)` a través
 *     del hook `useLandedCostPreview` en `src/hooks/useLandedCostPreview.ts`.
 *   ▸ El motor server-side (`_calculate_landed_cost_internal`) reemplaza por
 *     completo a `calcLandedCostV2` y garantiza paridad 1:1 con el "freeze"
 *     de `liquidar_y_cerrar_embarque`.
 *
 * Este archivo se conserva ÚNICAMENTE como red de seguridad temporal y para
 * la suite de tests unitarios histórica (`src/test/landedCost.*.test.ts`).
 * NO añadir nuevos consumidores. Las llamadas existentes se migrarán al RPC
 * en una ronda posterior, componente por componente.
 * ============================================================================
 *
 * Landed Cost Engine v2 — Canonical Pure Function (legacy)
 * ============================================================================
 *
 * Single Source of Truth (frontend) for shipment-centric landed cost.
 * Mirrors the backend RPC `liquidar_y_cerrar_embarque` 1:1.
 *
 * Design principles:
 *   1. PURE — no React, no Supabase, no I/O. Deterministic given inputs.
 *   2. DECIMAL — all monetary math via decimal.js (no floating point drift).
 *   3. CONFIGURABLE — IVA, TLC, aplica_costeo_inventario, trm_pago honored.
 *   4. CONTRACT-TESTABLE — same inputs ⇒ same outputs as the SQL RPC.
 *
 * Status: SCAFFOLD (Phase 1 kickoff). This file establishes:
 *   - Input/output type contracts
 *   - Decimal-based primitives (sum, mul, div, allocate)
 *   - Skeleton of `calcLandedCostV2` with TODO markers
 *
 * NOT YET IMPLEMENTED (next prompt):
 *   - Largest Remainder Method for penny-perfect allocation
 *   - IVA computation (CIF + Arancel) * iva%
 *   - TLC override (gravamen = 0)
 *   - aplica_costeo_inventario filtering
 *   - trm_pago override per gasto
 *
 * Migration target for:
 *   - src/hooks/useShipmentLandedCost.ts (v2-partial, will be re-exported)
 *   - src/hooks/useInvoiceMath.ts → calcLandedCosts (deprecated)
 * ============================================================================
 */

import Decimal from "decimal.js";

// ── Decimal global config ────────────────────────────────────────────────────
// Precision well above what monetary math requires; rounding only at boundaries.
//
// ⚠️ ROUNDING MODE: ROUND_HALF_EVEN ("Banker's Rounding")
// ─────────────────────────────────────────────────────────
// We harmonize with PostgreSQL's native `ROUND(numeric, n)` which uses
// HALF_EVEN. This guarantees 1:1 parity with the SQL RPC
// `liquidar_y_cerrar_embarque` — the same input produces the same cent in
// both engines (frontend preview === backend freeze).
//
// HALF_EVEN is also statistically unbiased (no systematic upward drift),
// which is the recommended mode for financial accumulation per IEEE 754
// and is the default in many international accounting standards (IFRS).
//
// Example: 12345.005 → 12345.00 (banker), not 12345.01 (commercial HALF_UP).
Decimal.set({ precision: 28, rounding: Decimal.ROUND_HALF_EVEN });

// ─────────────────────────────────────────────────────────────────────────────
// Public Types
// ─────────────────────────────────────────────────────────────────────────────

export type Currency = string; // "COP" | "USD" | "EUR" | ...
export type ProrationBase = "Peso" | "Valor" | "CBM"; // CBM lands in Phase 3
export type EngineVersion = "v1" | "v2";

/** Currency code → rate to COP. e.g. { USD: 4200, EUR: 4600, COP: 1 } */
export type TRMMap = Record<Currency, number>;

export interface EngineDocumentInput {
  id: string;
  moneda?: Currency | null;
  /** Legacy doc-level charges (back-compat with registro_documentos). */
  costo_flete?: number | string | null;
  seguro?: number | string | null;
  otros_cargos?: number | string | null;
  impuestos?: number | string | null;
}

export interface EngineItemInput {
  id: string;
  documento_id: string;
  /** Descriptors (UI-only, ignored by the math engine). Populated by
   *  `clonar_embarque_a_proforma` so the simulator can render per-SKU panels
   *  without extra DB lookups. Optional & nullable for back-compat. */
  sku?: string | null;
  descripcion?: string | null;
  unidad_medida?: string | null;
  precio_unitario?: number | string | null;
  cantidad?: number | string | null;
  sub_total?: number | string | null;
  peso_bruto_kg?: number | string | null;
  /** CBM for volumetric proration (Phase 3). */
  volumen_cbm?: number | string | null;
  /**
   * Free Trade Agreement override → gravamen = 0 if true.
   * @deprecated B-02B — usa `beneficio_aduanero='TLC_PURO'` en su lugar.
   *   Se conserva para compat hacia atrás: si está en `true` y
   *   `beneficio_aduanero` viene null/'GENERAL', el motor lo promueve
   *   internamente a 'TLC_PURO'.
   */
  aplica_tlc?: boolean | null;
  /**
   * B-02B — Régimen aduanero del ítem. Determina los efectos de Arancel/IVA
   * vía `applyBenefitMatrix`. Default 'GENERAL' si no se provee.
   */
  beneficio_aduanero?: BenefitMatrix | null;
  /** Per-item IVA override (overrides arancel_1881 lookup). */
  iva_override?: number | string | null;
  /** ICO — grados alcohólicos (0–100). */
  grados_alcoholicos?: number | string | null;
  /** ICO — litros de líquido por unidad. */
  litros_por_unidad?: number | string | null;
  /** ICO — tarifa COP por grado-alcohólico-litro. */
  tarifa_ico?: number | string | null;
  /** ICO multi-tipo: null/'BEBIDAS_ALCOHOLICAS' (g·t·l), 'VEHICULOS' (% CIF), 'CIGARRILLOS'. */
  ico_tipo?: string | null;
  /**
   * Multi-currency: moneda específica del ítem. Si se provee, tiene prioridad
   * sobre `documents[*].moneda` para el cálculo (ej. items_nuevos en simulador
   * que pueden ser USD, EUR, CNY independientes del documento sintético).
   */
  moneda?: Currency | null;
}

/**
 * B-02B — Matriz de regímenes aduaneros especiales colombianos.
 * Mantén sincronizado con el enum `public.beneficio_aduanero` en Postgres.
 */
export type BenefitMatrix = "GENERAL" | "TLC_PURO" | "LEY_1715" | "ZONA_FRANCA";

/**
 * B-02B — Resuelve los porcentajes efectivos de Arancel e IVA a partir
 * del régimen aduanero del ítem. Es una FUNCIÓN PURA, sin side effects.
 *
 * Reglas (matriz oficial):
 *   GENERAL      → arancel = lookup, IVA = lookup        (sin beneficio)
 *   TLC_PURO     → arancel = 0,      IVA = lookup        (TLC vigente)
 *   LEY_1715     → arancel = 0,      IVA = 0             (energías renovables)
 *   ZONA_FRANCA  → arancel = 0,      IVA = 0             (suspendidos)
 *
 * Inputs son los porcentajes de "lookup" (arancel_1881 / iva_override) y
 * la función decide si los respeta o los anula.
 */
export function applyBenefitMatrix(
  beneficio: BenefitMatrix,
  arancelPctLookup: Decimal,
  ivaPctLookup: Decimal,
): { arancelPct: Decimal; ivaPct: Decimal } {
  switch (beneficio) {
    case "TLC_PURO":
      return { arancelPct: new Decimal(0), ivaPct: ivaPctLookup };
    case "LEY_1715":
    case "ZONA_FRANCA":
      return { arancelPct: new Decimal(0), ivaPct: new Decimal(0) };
    case "GENERAL":
    default:
      return { arancelPct: arancelPctLookup, ivaPct: ivaPctLookup };
  }
}

/**
 * Resuelve el régimen efectivo del ítem combinando el campo nuevo
 * `beneficio_aduanero` con el flag legacy `aplica_tlc`. Si el régimen viene
 * vacío y `aplica_tlc=true`, se promueve a 'TLC_PURO' (back-compat).
 */
export function resolveBenefit(item: Pick<EngineItemInput, "beneficio_aduanero" | "aplica_tlc">): BenefitMatrix {
  const explicit = item.beneficio_aduanero;
  if (explicit && explicit !== "GENERAL") return explicit;
  if (explicit === "GENERAL") return "GENERAL";
  // explicit es null/undefined → caer al flag legacy.
  return item.aplica_tlc === true ? "TLC_PURO" : "GENERAL";
}

export interface EngineGastoInput {
  id: string;
  concepto: string;
  monto: number | string;
  moneda: Currency;
  base_prorrateo: ProrationBase;
  /** Payment-time exchange rate; falls back to TRMMap[moneda] if null. */
  trm_pago?: number | string | null;
  /** If false, the gasto is excluded from inventory cost (financial-only). */
  aplica_costeo_inventario?: boolean;
  /**
   * DIAN — Si true, el gasto entra a la BASE GRAVABLE (CIF aduanero) sobre la
   * cual se calcula Arancel + IVA. Por norma DIAN solo flete y seguro
   * INTERNACIONALES deben llevarlo en true. Default false (postura conservadora).
   * Si viene null/undefined, el motor aplica autodetección por regex sobre el
   * concepto (ver `esGastoInternacionalPorConcepto`).
   */
  incluir_en_base_gravable?: boolean | null;
}

/**
 * Autodetector conservador: marca un gasto como "internacional" (sumable a la
 * base gravable DIAN) si su concepto matchea flete/seguro internacional o
 * transporte marítimo/aéreo, EXCLUYENDO términos locales/terrestres.
 *
 * Usado como fallback cuando el flag explícito `incluir_en_base_gravable`
 * viene en null/undefined (datos legacy o ingestas sin clasificar).
 */
export function esGastoInternacionalPorConcepto(concepto: string | null | undefined): boolean {
  const c = (concepto ?? "").trim();
  if (!c) return false;
  const internacional = /(flete|freight|ocean|maritim|aere|air\s*freight|sea\s*freight|seguro|insurance|cargo\s*insurance)/i;
  const local = /(local|terrestre|interno|nacional|inland|trucking|sia|comisi[oó]n)/i;
  return internacional.test(c) && !local.test(c);
}

/** Per-item arancel & iva percentages keyed by item.id. */
export interface ComplianceRates {
  /** Gravamen (arancel) % — e.g. 5 means 5%. */
  arancelPctByItemId: Record<string, number>;
  /** IVA % — e.g. 19 means 19%. Falls back to item.iva_override or 19. */
  ivaPctByItemId: Record<string, number>;
}

export interface CostEngineInput {
  documents: EngineDocumentInput[];
  items: EngineItemInput[];
  gastos: EngineGastoInput[];
  trmMap: TRMMap;
  /** DIAN aduana TRM — overrides USD rate for FOB & CIF when present. */
  trmAduana?: number | null;
  compliance: ComplianceRates;
  /** Engine version stamp for audit trail. Defaults to 'v2'. */
  engineVersion?: EngineVersion;
}

export interface ItemLandedCost {
  itemId: string;
  monedaOrigen: Currency;
  /** Allocation factors (0..1). */
  factorValor: number;
  factorPeso: number;
  factorCBM: number;
  /** COP-normalized prorated charges. */
  gastosPesoAsignadosCOP: number;
  gastosValorAsignadosCOP: number;
  gastosCBMAsignadosCOP: number;
  /**
   * R6 (paridad RPC) — Suma de pools físicos (Peso + CBM). Espejo de
   * `alloc_peso_cop` en el RPC, que agrupa ambos físicos en un solo campo
   * para retrocompatibilidad de UI legacy. NO reemplaza los granulares.
   */
  gastosFisicosAsignadosCOP: number;
  /** Cascade. */
  fobItemCOP: number;
  cifItemCOP: number;
  arancelItemCOP: number;
  ivaItemCOP: number;
  landedCostTotalCOP: number;
  landedCostUnitarioCOP: number;
}

export interface CostEngineGlobals {
  fobTotalCOP: Decimal.Value;
  pesoTotalKg: Decimal.Value;
  cbmTotal: Decimal.Value;
  /** Pool financiero (todos los gastos por Peso). */
  gastosPesoCOP: Decimal.Value;
  /** Pool financiero (todos los gastos por Valor). */
  gastosValorCOP: Decimal.Value;
  gastosCBMCOP: Decimal.Value;
  /** R6 (paridad RPC) — Pool físico agregado: Peso + CBM. */
  gastosFisicosCOP: Decimal.Value;
  /** Pool BASE GRAVABLE DIAN (solo internacionales por Peso). */
  gastosPesoGravableCOP: Decimal.Value;
  /** Pool BASE GRAVABLE DIAN (solo internacionales por Valor). */
  gastosValorGravableCOP: Decimal.Value;
  /** Sum of all item landed costs (post-allocation reconciliation). */
  totalLandedCOP: Decimal.Value;
}

export interface CostEngineResult {
  engineVersion: EngineVersion;
  globals: CostEngineGlobals;
  items: ItemLandedCost[];
  /** Drift after Largest Remainder reconciliation. Should always be 0. */
  reconciliationDriftCOP: number;
  /**
   * True cuando la bolsa de "Peso" tuvo que prorratearse por Valor FOB
   * porque el embarque/ítems no tienen `peso_bruto_kg` declarado.
   * La UI puede usar este flag para mostrar una advertencia al usuario.
   */
  warningFallbackPesoActivado: boolean;
  /**
   * True cuando AL MENOS UN ítem COGS no tenía peso (peso parcial). En ese
   * caso TODA la bolsa de Peso se prorratea por Valor FOB para no penalizar
   * a quienes sí declararon peso. Postura aprobada por arquitectura.
   */
  warningPesoIncompleto: boolean;
  /** Lista de itemIds sin peso declarado — usar para listar en banner UI. */
  itemsSinPeso: string[];
  /**
   * R6 (paridad RPC) — True cuando AL MENOS UN ítem no tiene `volumen_cbm`
   * declarado. En ese caso TODA la bolsa CBM se prorratea por Valor FOB
   * (espejo simétrico del fallback de Peso).
   */
  warningCBMIncompleto: boolean;
  /** Lista de itemIds sin CBM declarado — usar para banner UI. */
  itemsSinCBM: string[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Decimal Primitives — internal helpers, exported for contract testing
// ─────────────────────────────────────────────────────────────────────────────

/** Safe coerce: anything → Decimal, treating null/undefined/NaN as 0. */
export function D(v: unknown): Decimal {
  if (v === null || v === undefined || v === "") return new Decimal(0);
  try {
    const d = new Decimal(typeof v === "number" ? v : String(v));
    return d.isFinite() ? d : new Decimal(0);
  } catch {
    return new Decimal(0);
  }
}

/** Sum a list of values into a single Decimal. */
export function dSum(values: ReadonlyArray<unknown>): Decimal {
  return values.reduce<Decimal>((acc, v) => acc.plus(D(v)), new Decimal(0));
}

/**
 * Currency rate lookup → returns the Decimal rate to convert `moneda` to COP.
 *
 * Business rules (mirror SQL RPC `liquidar_y_cerrar_embarque` exactly):
 *   1. COP (or null/empty)  → rate = 1
 *   2. USD                   → rate = trmAduana (DIAN-mandated for FOB/CIF).
 *      If trmAduana is missing/invalid, fall back to trmMap.USD.
 *   3. Any other currency    → rate = trmMap[moneda].
 *      If the rate is missing, THROW — silently using 1 would corrupt the
 *      landed cost (a CNY invoice prorated as if it were COP).
 *
 * @throws Error if a non-COP/non-USD currency has no rate available.
 */
export function getRate(
  moneda: Currency | null | undefined,
  trmMap: TRMMap,
  trmAduana?: number | null,
): Decimal {
  if (!moneda) return new Decimal(1);
  const key = moneda.toUpperCase().trim();
  if (key === "COP") return new Decimal(1);

  if (key === "USD") {
    if (trmAduana && trmAduana > 0) return D(trmAduana);
    const usdMarket = trmMap[key];
    if (usdMarket && Number(usdMarket) > 0) return D(usdMarket);
    // Soft-fail: avoid crashing the UI. Caller should surface a "missing TRM" badge.
    console.warn(
      "[landedCostEngine] TRM no disponible para USD (ni Aduanera ni de mercado). Se aplica rate=0; cargue la TRM antes de liquidar.",
    );
    return new Decimal(0);
  }

  const rate = trmMap[key];
  if (rate == null || Number(rate) <= 0) {
    // Soft-fail: returning 0 prevents the modal from blanking when an exotic
    // currency (e.g. JPY, CNY) appears in a gasto/doc but no TRM is loaded.
    // The UI should warn the user via the "Total en Limbo" badge / KPIs.
    console.warn(
      `[landedCostEngine] Tasa de cambio no encontrada para ${key}. ` +
        `Se aplica rate=0 — cargue la TRM histórica antes de liquidar.`,
    );
    return new Decimal(0);
  }
  return D(rate);
}

/** Convert a monetary amount to COP using effective rate (with aduana override). */
export function toCOP(
  amount: unknown,
  moneda: Currency | null | undefined,
  trmMap: TRMMap,
  trmAduana?: number | null,
): Decimal {
  return D(amount).times(getRate(moneda, trmMap, trmAduana));
}

/**
 * Round a Decimal to N decimal places using HALF_EVEN (Banker's Rounding).
 * Matches PostgreSQL `ROUND(numeric, n)` for cross-engine parity.
 * Returns a Decimal — caller decides when to convert to Number.
 */
export function roundN(value: Decimal, places = 2): Decimal {
  return value.toDecimalPlaces(places, Decimal.ROUND_HALF_EVEN);
}

/** Convenience: round to 2 decimals and return a primitive number. */
export function round2(value: Decimal): number {
  return roundN(value, 2).toNumber();
}

/**
 * Allocate a total amount across weights using simple proportional math.
 * Used internally for non-monetary or pre-reconciliation calculations.
 *
 * For MONETARY allocation that must reconcile penny-perfect, use
 * `allocateLargestRemainder` instead.
 */
export function allocateProportional(
  total: Decimal,
  weights: ReadonlyArray<Decimal>,
): Decimal[] {
  const totalWeight = weights.reduce<Decimal>((a, w) => a.plus(w), new Decimal(0));
  if (totalWeight.isZero()) {
    if (weights.length === 0) return [];
    const equal = total.dividedBy(weights.length);
    return weights.map(() => equal);
  }
  return weights.map((w) => total.times(w).dividedBy(totalWeight));
}

/**
 * Largest Remainder Method (a.k.a. Hamilton method) for penny-perfect
 * allocation of a monetary total across weighted buckets.
 *
 * Algorithm:
 *   1. Compute exact share = total * weight / Σweights (full precision).
 *   2. Floor each share to N decimal places → base allocation.
 *   3. Compute remainder = exactShare − baseAllocation per bucket.
 *   4. shortfall = total − Σ(baseAllocations) (always ≥ 0 with FLOOR).
 *   5. Express shortfall as integer count of "smallest units" (10^-places).
 *   6. Distribute one unit at a time to buckets with the largest remainder.
 *      Ties are broken by lexicographic comparison of `ids[i]` — this matches
 *      Postgres `ORDER BY remainder DESC, item_id ASC` and guarantees the SQL
 *      RPC and the TS engine award the same cent to the same item.
 *
 * Guarantee: Σ(allocations) === total to N decimal places, exactly.
 *
 * Edge cases:
 *   - Empty weights → empty array.
 *   - All weights zero → equal split, then reconciled the same way.
 *   - Negative total or weights → undefined behavior; caller must validate.
 *
 * @param total    Amount to distribute (Decimal, assumed ≥ 0).
 * @param weights  Per-bucket weights (assumed ≥ 0).
 * @param places   Decimal places to reconcile against. Default 2 (centavos COP).
 * @param ids      Optional stable identifiers (e.g. UUIDs) for tie-breaking.
 *                 If omitted, falls back to original index order. Use UUIDs
 *                 to guarantee SQL parity.
 */
export function allocateLargestRemainder(
  total: Decimal,
  weights: ReadonlyArray<Decimal>,
  places = 2,
  ids?: ReadonlyArray<string>,
): Decimal[] {
  const n = weights.length;
  if (n === 0) return [];
  if (total.isZero()) return weights.map(() => new Decimal(0));

  const totalWeight = weights.reduce<Decimal>((a, w) => a.plus(w), new Decimal(0));

  // Step 1: exact (un-rounded) shares.
  const exactShares: Decimal[] = totalWeight.isZero()
    ? weights.map(() => total.dividedBy(n))
    : weights.map((w) => total.times(w).dividedBy(totalWeight));

  // Step 2 + 3: floor to N places + capture remainders.
  const base: Decimal[] = exactShares.map((s) => s.toDecimalPlaces(places, Decimal.ROUND_FLOOR));
  const remainders: Decimal[] = exactShares.map((s, i) => s.minus(base[i]));

  // Step 4 + 5: compute shortfall in smallest units (e.g. cents).
  // Target uses HALF_EVEN to match Postgres ROUND() — guarantees parity with
  // the SQL RPC's `_target_peso_cop := ROUND(_gastos_peso_cop, 2)` line.
  const targetTotal = total.toDecimalPlaces(places, Decimal.ROUND_HALF_EVEN);
  const allocatedSum = base.reduce<Decimal>((a, b) => a.plus(b), new Decimal(0));
  const shortfall = targetTotal.minus(allocatedSum);
  const unit = new Decimal(10).pow(-places);                 // e.g. 0.01
  const unitsToDistribute = shortfall.dividedBy(unit).round().toNumber();

  if (unitsToDistribute <= 0) return base;

  // Step 6: rank by remainder DESC, tie-break by id ASC (lexicographic) to
  // mirror SQL `ORDER BY rem_peso DESC, item_id`. If ids are not supplied,
  // fall back to original index for backward compatibility.
  const order = remainders
    .map((r, i) => ({ i, r, key: ids?.[i] ?? String(i).padStart(12, "0") }))
    .sort((a, b) => {
      const cmp = b.r.comparedTo(a.r);
      if (cmp !== 0) return cmp;
      // Lexicographic UUID comparison — identical to Postgres text/uuid sort.
      if (a.key < b.key) return -1;
      if (a.key > b.key) return 1;
      return 0;
    });

  for (let k = 0; k < unitsToDistribute; k++) {
    const idx = order[k % n].i; // wrap defensively if shortfall > n units
    base[idx] = base[idx].plus(unit);
  }

  return base;
}


// ─────────────────────────────────────────────────────────────────────────────
// Main Engine — SCAFFOLD ONLY
// Full IVA / TLC / aplica_costeo_inventario / Largest Remainder land next prompt.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Canonical Landed Cost calculator. Pure function — call with full snapshot.
 *
 * IMPLEMENTED:
 *   ✅ Decimal-based FOB / Peso / CBM totals (no float drift)
 *   ✅ Decimal-based gastos pool aggregation by base_prorrateo
 *   ✅ Currency normalization with trm_aduana priority
 *   ✅ aplica_costeo_inventario filter
 *   ✅ trm_pago override per gasto (falls back to TRMMap)
 *   ✅ Arancel cascade: cifItemCOP * (arancelPct / 100)
 *   ✅ TLC override: arancelPct = 0 when item.aplica_tlc === true
 *   ✅ IVA cascade: (cifItemCOP + arancelItemCOP) * (ivaPct / 100)
 *   ✅ Largest Remainder Method for gastos allocation (penny-perfect)
 *   ✅ HALF_UP rounding to 2 decimals on all monetary outputs
 *   ✅ Drift telemetry (reconciliationDriftCOP — should always be 0)
 *
 * PENDING (Phase 3):
 *   ⏳ CBM-based proration UI exposure (engine already supports it)
 *
 * @deprecated ENG-01 (2026-04-30) — La matemática se ejecuta ahora en
 *   PostgreSQL vía el RPC `calculate_landed_cost_preview`. Use el hook
 *   `useLandedCostPreview` (src/hooks/useLandedCostPreview.ts). Este
 *   símbolo se conserva solo para retrocompatibilidad de tests unitarios
 *   históricos y como red de seguridad mientras se migran los consumidores.
 *   No añadir nuevas llamadas.
 */
export function calcLandedCostV2(input: CostEngineInput): CostEngineResult {
  const { documents, items, gastos, trmMap, trmAduana, compliance, engineVersion = "v2" } = input;

  const docMap = new Map(documents.map((d) => [d.id, d]));

  // ── Globals: FOB / Peso / CBM ──────────────────────────────────────────
  let fobTotalCOP = new Decimal(0);
  let pesoTotalKg = new Decimal(0);
  let cbmTotal = new Decimal(0);

  const itemFobCOP = new Map<string, Decimal>();
  for (const item of items) {
    const doc = docMap.get(item.documento_id);
    const subCOP = toCOP(item.sub_total, doc?.moneda, trmMap, trmAduana);
    itemFobCOP.set(item.id, subCOP);
    fobTotalCOP = fobTotalCOP.plus(subCOP);
    pesoTotalKg = pesoTotalKg.plus(D(item.peso_bruto_kg));
    cbmTotal = cbmTotal.plus(D(item.volumen_cbm));
  }

  // ── Gastos pools (aplica_costeo_inventario + trm_pago honored) ─────────
  // Mantenemos DOS pools paralelos:
  //   - Pool TOTAL (financiero) → entra al CIF contable / Landed Cost
  //   - Pool BASE GRAVABLE (DIAN) → solo internacionales, entra al cálculo
  //     de Arancel + IVA (no se mete flete terrestre, SIA, etc.)
  let gastosPesoCOP = new Decimal(0);
  let gastosValorCOP = new Decimal(0);
  let gastosCBMCOP = new Decimal(0);
  let gastosPesoGravableCOP = new Decimal(0);
  let gastosValorGravableCOP = new Decimal(0);

  // Mutex de deduplicación: si el flete/seguro YA llegó como gasto explícito
  // (vía gastos_embarque del BL/manual/n8n), debemos ignorar las casillas
  // legacy `costo_flete` / `seguro` de la DIM (registro_documentos) para
  // evitar doble contabilización del mismo costo logístico/asegurador.
  let fleteYaContabilizado = false;
  let seguroYaContabilizado = false;

  for (const g of gastos) {
    if (g.aplica_costeo_inventario === false) continue;
    const rate = g.trm_pago != null && Number(g.trm_pago) > 0
      ? D(g.trm_pago)
      : getRate(g.moneda, trmMap, trmAduana);
    const montoCOP = D(g.monto).times(rate);

    // Resolver si entra a base gravable DIAN:
    //   - Si el flag explícito viene definido (true/false) → respetarlo.
    //   - Si viene null/undefined → autodetección por regex sobre el concepto
    //     (postura conservadora: solo internacionales).
    const esGravable = g.incluir_en_base_gravable == null
      ? esGastoInternacionalPorConcepto(g.concepto)
      : g.incluir_en_base_gravable === true;

    if (g.base_prorrateo === "Peso") {
      gastosPesoCOP = gastosPesoCOP.plus(montoCOP);
      if (esGravable) gastosPesoGravableCOP = gastosPesoGravableCOP.plus(montoCOP);
      if (/flete|freight|ocean|transporte|maritim|aere/i.test(g.concepto ?? "")) {
        fleteYaContabilizado = true;
      }
    } else if (g.base_prorrateo === "CBM") {
      gastosCBMCOP = gastosCBMCOP.plus(montoCOP);
    } else {
      gastosValorCOP = gastosValorCOP.plus(montoCOP);
      if (esGravable) gastosValorGravableCOP = gastosValorGravableCOP.plus(montoCOP);
      if (/seguro|insurance|cargo\s*insurance/i.test(g.concepto ?? "")) {
        seguroYaContabilizado = true;
      }
    }
  }

  // Legacy doc-level charges (registro_documentos.costo_flete / seguro /
  // otros_cargos) son flete y seguro INTERNACIONALES por construcción
  // (vienen de la factura del proveedor extranjero o del BL). Por tanto
  // entran tanto al pool total como al pool gravable DIAN.
  for (const doc of documents) {
    const rate = getRate(doc.moneda, trmMap, trmAduana);
    if (!fleteYaContabilizado) {
      const fleteCOP = D(doc.costo_flete).times(rate);
      gastosPesoCOP = gastosPesoCOP.plus(fleteCOP);
      gastosPesoGravableCOP = gastosPesoGravableCOP.plus(fleteCOP);
    }
    if (!seguroYaContabilizado) {
      const seguroCOP = D(doc.seguro).times(rate);
      gastosValorCOP = gastosValorCOP.plus(seguroCOP);
      gastosValorGravableCOP = gastosValorGravableCOP.plus(seguroCOP);
    }
    // `otros_cargos` es un agregado residual de la DIM (gastos legales,
    // tasas, comisiones SIA). NO entra a la base gravable DIAN por defecto.
    gastosValorCOP = gastosValorCOP.plus(D(doc.otros_cargos).times(rate));
  }

  // ── Stable item order for deterministic Largest Remainder output ───────
  const orderedItems = items.slice();
  const itemIds = orderedItems.map((it) => it.id);
  const weightsValor = orderedItems.map((it) => itemFobCOP.get(it.id) ?? new Decimal(0));
  const weightsPesoRaw = orderedItems.map((it) => D(it.peso_bruto_kg));
  const weightsCBM = orderedItems.map((it) => D(it.volumen_cbm));

  // ── Detección granular de peso parcial ───────────────────────────────────
  // Si AL MENOS UN ítem no tiene peso declarado (>0), TODA la bolsa de Peso
  // se prorratea por Valor FOB. Decisión arquitectónica: postura justa que
  // no penaliza a quienes sí declararon peso (alternativa: prorratear solo
  // entre los que tienen peso, pero eso sobre-asigna costo a esos ítems).
  const itemsSinPeso: string[] = orderedItems
    .filter((it) => D(it.peso_bruto_kg).lte(0))
    .map((it) => it.id);
  const hayPesoParcial = itemsSinPeso.length > 0 && orderedItems.length > 0;

  // ── R6 (paridad RPC): Detección granular de CBM parcial ──────────────────
  // Espejo simétrico de la regla de Peso. Si algún ítem no declara CBM,
  // toda la bolsa CBM cae a Valor FOB.
  const itemsSinCBM: string[] = orderedItems
    .filter((it) => D(it.volumen_cbm).lte(0))
    .map((it) => it.id);
  const hayCBMParcial = itemsSinCBM.length > 0 && orderedItems.length > 0;

  // Fallback completo de peso → cuando NINGÚN ítem tiene peso (caso histórico).
  const warningFallbackPesoActivado =
    pesoTotalKg.isZero() && (!gastosPesoCOP.isZero() || !gastosPesoGravableCOP.isZero());

  // Fallback granular de peso → cuando hay peso parcial (caso nuevo, Sprint).
  const warningPesoIncompleto = hayPesoParcial && !pesoTotalKg.isZero();
  // Fallback granular de CBM → cuando hay CBM parcial (no todos vacíos).
  const warningCBMIncompleto = hayCBMParcial && !cbmTotal.isZero();

  // Decisión efectiva de pesos para prorrateo: si hay cualquier vacío, usar Valor.
  const usarValorParaPeso = pesoTotalKg.isZero() || hayPesoParcial;
  const usarValorParaCBM  = cbmTotal.isZero()    || hayCBMParcial;
  const weightsPesoEffective = usarValorParaPeso ? weightsValor : weightsPesoRaw;
  const weightsCBMEffective  = usarValorParaCBM  ? weightsValor : weightsCBM;

  // ── Allocations — POOLS PARALELOS (financiero + base gravable). ────────
  // ⚠️ B-01 (post-mortem Solartech/TitanX): NO confiar en el `idx` posicional.
  // `allocateLargestRemainder` devuelve un array alineado posicionalmente con
  // `itemIds`, pero la fase de cascade puede iterar `orderedItems` en orden
  // distinto (ahora idénticos, mañana no). Convertimos a `Map<itemId, Decimal>`
  // para que TODA consulta posterior se haga por id estable, no por índice.
  const toAllocMap = (
    arr: Decimal[],
  ): Map<string, Decimal> =>
    new Map(itemIds.map((id, i) => [id, arr[i] ?? new Decimal(0)]));

  const allocPesoTotal = toAllocMap(
    allocateLargestRemainder(gastosPesoCOP, weightsPesoEffective, 2, itemIds),
  );
  const allocValorTotal = toAllocMap(
    allocateLargestRemainder(gastosValorCOP, weightsValor, 2, itemIds),
  );
  const allocCBMTotal = toAllocMap(
    allocateLargestRemainder(gastosCBMCOP, weightsCBMEffective, 2, itemIds),
  );
  const allocPesoGravable = toAllocMap(
    allocateLargestRemainder(gastosPesoGravableCOP, weightsPesoEffective, 2, itemIds),
  );
  const allocValorGravable = toAllocMap(
    allocateLargestRemainder(gastosValorGravableCOP, weightsValor, 2, itemIds),
  );

  const ZERO = new Decimal(0);

  // ── Per-item cascade: FOB → CIF financiero / Base Gravable → Arancel → IVA ─
  const itemResults: ItemLandedCost[] = orderedItems.map((item) => {
    const doc = docMap.get(item.documento_id);
    const monedaOrigen = (doc?.moneda ?? "USD").toUpperCase().trim();
    const fobItemCOP = itemFobCOP.get(item.id) ?? new Decimal(0);
    const cantidad = D(item.cantidad);

    const factorValor = fobTotalCOP.isZero() ? new Decimal(0) : fobItemCOP.dividedBy(fobTotalCOP);
    const factorPeso = usarValorParaPeso
      ? factorValor
      : D(item.peso_bruto_kg).dividedBy(pesoTotalKg);
    const factorCBM = usarValorParaCBM ? factorValor : D(item.volumen_cbm).dividedBy(cbmTotal);

    // 🔑 Lookup por itemId — NO por idx. Inmune a reordenamientos.
    const gastosPesoAsignadosCOP = allocPesoTotal.get(item.id) ?? ZERO;
    const gastosValorAsignadosCOP = allocValorTotal.get(item.id) ?? ZERO;
    const gastosCBMAsignadosCOP = allocCBMTotal.get(item.id) ?? ZERO;

    // CIF FINANCIERO (contable) — incluye TODOS los gastos prorrateados.
    const cifItemCOP = fobItemCOP
      .plus(gastosPesoAsignadosCOP)
      .plus(gastosValorAsignadosCOP)
      .plus(gastosCBMAsignadosCOP);

    // BASE GRAVABLE DIAN — SOLO FOB + flete/seguro internacional.
    // No incluye gastos locales (terrestre, SIA, otros_cargos), por norma DIAN.
    const baseGravableItemCOP = fobItemCOP
      .plus(allocPesoGravable.get(item.id) ?? ZERO)
      .plus(allocValorGravable.get(item.id) ?? ZERO);

    // ── B-02B: Matriz de Beneficios Aduaneros ──────────────────────────
    // Resuelve el régimen efectivo (incluyendo back-compat con `aplica_tlc`)
    // y aplica los efectos de Arancel/IVA via `applyBenefitMatrix`.
    const arancelPctLookup = D(compliance.arancelPctByItemId[item.id] ?? 0);
    const ivaPctLookup = item.iva_override != null && item.iva_override !== ""
      ? D(item.iva_override)
      : D(compliance.ivaPctByItemId[item.id] ?? 0);
    const beneficio = resolveBenefit(item);
    const { arancelPct, ivaPct } = applyBenefitMatrix(
      beneficio,
      arancelPctLookup,
      ivaPctLookup,
    );

    const arancelItemCOP = baseGravableItemCOP.times(arancelPct).dividedBy(100);
    // IVA = (Base Gravable + Arancel) * iva%.
    const ivaItemCOP = baseGravableItemCOP.plus(arancelItemCOP).times(ivaPct).dividedBy(100);

    // Landed Cost contable (NIIF / NIC 2 — Colombia):
    // El IVA de importación es DESCONTABLE (no capitalizable al inventario).
    // Solo el CIF financiero + Arancel forman parte del costo del inventario.
    // `ivaItemCOP` se mantiene calculado y expuesto para reportes de flujo de caja.
    const landedCostTotalCOP = cifItemCOP.plus(arancelItemCOP);
    const landedCostUnitarioCOP = cantidad.isZero()
      ? new Decimal(0)
      : landedCostTotalCOP.dividedBy(cantidad);

    return {
      itemId: item.id,
      monedaOrigen,
      factorValor: factorValor.toNumber(),
      factorPeso: factorPeso.toNumber(),
      factorCBM: factorCBM.toNumber(),
      gastosPesoAsignadosCOP: round2(gastosPesoAsignadosCOP),
      gastosValorAsignadosCOP: round2(gastosValorAsignadosCOP),
      gastosCBMAsignadosCOP: round2(gastosCBMAsignadosCOP),
      gastosFisicosAsignadosCOP: round2(gastosPesoAsignadosCOP.plus(gastosCBMAsignadosCOP)),
      fobItemCOP: round2(fobItemCOP),
      cifItemCOP: round2(cifItemCOP),
      arancelItemCOP: round2(arancelItemCOP),
      ivaItemCOP: round2(ivaItemCOP),
      landedCostTotalCOP: round2(landedCostTotalCOP),
      landedCostUnitarioCOP: roundN(landedCostUnitarioCOP, 4).toNumber(),
    };
  });

  // ── Reconciliation drift telemetry ─────────────────────────────────────
  const sumPeso = itemResults.reduce<Decimal>((a, r) => a.plus(D(r.gastosPesoAsignadosCOP)), new Decimal(0));
  const sumValor = itemResults.reduce<Decimal>((a, r) => a.plus(D(r.gastosValorAsignadosCOP)), new Decimal(0));
  const sumCBM = itemResults.reduce<Decimal>((a, r) => a.plus(D(r.gastosCBMAsignadosCOP)), new Decimal(0));
  const driftPeso = roundN(gastosPesoCOP, 2).minus(sumPeso).abs();
  const driftValor = roundN(gastosValorCOP, 2).minus(sumValor).abs();
  const driftCBM = roundN(gastosCBMCOP, 2).minus(sumCBM).abs();
  const reconciliationDriftCOP = round2(driftPeso.plus(driftValor).plus(driftCBM));

  const totalLandedCOP = itemResults
    .reduce<Decimal>((a, r) => a.plus(D(r.landedCostTotalCOP)), new Decimal(0));

  return {
    engineVersion,
    globals: {
      fobTotalCOP,
      pesoTotalKg,
      cbmTotal,
      gastosPesoCOP,
      gastosValorCOP,
      gastosCBMCOP,
      gastosFisicosCOP: gastosPesoCOP.plus(gastosCBMCOP),
      gastosPesoGravableCOP,
      gastosValorGravableCOP,
      totalLandedCOP,
    },
    items: itemResults,
    reconciliationDriftCOP,
    warningFallbackPesoActivado,
    warningPesoIncompleto,
    warningCBMIncompleto,
    itemsSinPeso,
    itemsSinCBM,
  };
}
